import type { MobileNavRow } from './useMobileNav'

/**
 * The admin-authored header, fetched once per render and shared by both
 * surfaces.
 *
 * ONE request for desktop and mobile together. The header is on every
 * server-rendered page, so this is the most cost-sensitive query the storefront
 * makes — and the named `useAsyncData` key is what makes it one request rather
 * than one per consumer: `Header.vue` and `HeaderMobileNavDrawer.vue` both call
 * this and Nuxt serves the second from the payload, the same trick
 * `useHomepageCountdown` uses to piggyback on `useHomepageContent`.
 *
 * `server: true` because the header must be in the server-rendered HTML — the
 * navigation is content, and deferring it would ship a page whose menu appears
 * a beat late on every route.
 */

export interface HeaderMenuApiLink {
  label: string
  href: string
  imageUrl?: string | null
}

export interface HeaderMenuApiSection {
  id: string
  heading: string
  href: string | null
  pinned: boolean
  defaultOpen: boolean
  twoColumns: boolean
  widthPx: number
  items: HeaderMenuApiLink[]
}

export interface HeaderMenuApiEntry {
  id: string
  label: string
  href: string | null
  slot: string
  icon: string | null
  thumbUrl: string | null
  sections: HeaderMenuApiSection[]
  cards: HeaderMenuApiLink[]
}

export interface HeaderMenuApi {
  surface: string
  entries: HeaderMenuApiEntry[]
}

export function useHeaderMenus() {
  const { data, refresh } = useAsyncData(
    'headerMenus',
    async () => (await useGraphqlQuery('headerMenus')).data.headerMenus as HeaderMenuApi[],
    {
      server: true,
      default: () => [] as HeaderMenuApi[],
      /**
       * Two call sites join the in-flight request instead of replacing it.
       *
       * The default is `cancel`: a second `useAsyncData` on a key that is
       * already loading ABORTS the first and starts again. The header has three
       * call sites in one render — this plugin, `Header.vue` and the drawer —
       * so the default turned one query into a chain of cancel-and-retry.
       */
      dedupe: 'defer',
      /**
       * Serve a call site that arrives AFTER the fetch has settled from what
       * the fetch already produced.
       *
       * Nuxt's default only consults the payload while hydrating; on the server
       * it reads `nuxtApp.static.data`, which `payloadExtraction: false` leaves
       * permanently empty. So `Header.vue` asking for this key during render —
       * after the plugin has already resolved it — fell through to a second
       * network round trip, and the drawer would have made a third. Measured at
       * 3 backend `headerMenus` requests per server-rendered page against 1 for
       * every other query on the page.
       *
       * `refresh()` must still reach the network, which is what the cause check
       * preserves: an explicit refresh (or `refreshNuxtData`) skips the cache,
       * everything else reuses it.
       */
      getCachedData: (key, nuxtApp, ctx) =>
        (ctx.cause === 'refresh:manual' || ctx.cause === 'refresh:hook')
          ? undefined
          : nuxtApp.payload.data[key] as HeaderMenuApi[] | undefined,
    },
  )

  const bySurface = (surface: 'DESKTOP' | 'MOBILE') =>
    computed<HeaderMenuApiEntry[]>(() => data.value?.find(menu => menu.surface === surface)?.entries ?? [])

  return { menus: data, bySurface, refresh }
}

/**
 * Map one authored entry onto the shape the mobile drawer already renders.
 *
 * The drawer's own types are unchanged on purpose: this is a data-source swap,
 * not a rewrite, so every component below it keeps compiling and the static
 * tree stays usable as a fallback.
 *
 * `pinned` sections become the panel's pinned block; everything else becomes a
 * collapsible group. That is the same split the seed wrote, so a menu authored
 * in the Dashboard round-trips to exactly what the storefront shipped before.
 */
export function toMobileNavRows(entries: HeaderMenuApiEntry[]): MobileNavRow[] {
  return entries.map((entry) => {
    // Sections keep the order the backend returned them in — it sorts by the
    // authored `position` — and `pinned` only decides how a section RENDERS,
    // never where it sits. Dropping the heading is what makes it flat; the
    // panel component reads that, not a separate flag.
    const sections = entry.sections.map(section => ({
      id: section.id,
      heading: section.pinned ? undefined : section.heading,
      href: section.pinned ? undefined : (section.href ?? undefined),
      defaultOpen: !section.pinned && section.defaultOpen,
      links: section.items.map(item => ({ label: item.label, href: item.href })),
    }))

    return {
      // The database id, not a slug: UI state keys off this, and an id is stable
      // across a rename in a way a translated label never is.
      key: entry.id,
      label: entry.label,
      thumb: entry.thumbUrl ?? undefined,
      icon: entry.icon ?? undefined,
      // A row either navigates or opens a panel, never both — the rule the
      // backend enforces on save, mirrored here so a malformed row degrades to a
      // link rather than rendering an empty panel.
      href: sections.length ? undefined : (entry.href ?? undefined),
      panel: sections.length ? { sections } : undefined,
    }
  })
}

export interface DesktopMegaColumn {
  heading: string
  twoColumns?: boolean
  links: Array<{ label: string, href: string }>
}

export interface DesktopMegaMenu {
  key: string
  /** Inline `grid-template-columns`, NOT a class — see below. */
  gridTemplate: string
  columns: DesktopMegaColumn[]
  cards: Array<{ image: string, label: string, href: string }>
}

/** Desktop promo-card track width, matching today's 220px cards. */
const CARD_TRACK = '220px'

/**
 * Map the authored desktop tree onto the mega-menu shape `Header.vue` renders.
 *
 * `gridTemplate` is an inline style rather than the `layoutClass` string the
 * header used to carry, and that is not a stylistic choice: UnoCSS generates
 * arbitrary classes by SCANNING SOURCE, so a template built at runtime —
 * `grid-cols-[200px_200px_1fr_220px]` assembled from admin data — would produce
 * no CSS at all and the panel would collapse to a single column. An inline
 * `grid-template-columns` needs no build-time knowledge, which is exactly what
 * makes the layout editable.
 *
 * The track list is derived from the authored structure: one track per column at
 * its own `widthPx`, a `1fr` spacer, then one track per promo card. That is what
 * lets an admin add or remove a column without silently rendering four columns
 * into a five-track grid.
 *
 * Keyed by the entry's DATABASE ID, which is also what the trigger row keys off,
 * so the two halves cannot drift. This used to zip the entries onto a fixed list
 * of hand-written trigger keys and bail out when the counts disagreed — a real
 * constraint at the time, because the triggers were markup and could not follow
 * an admin who added a menu. They render from the same entries now, so a menu
 * added in the Dashboard brings its own trigger with it.
 */
export function toDesktopMenus(entries: HeaderMenuApiEntry[]): DesktopMegaMenu[] {
  return entries.filter(entry => entry.sections.length > 0).map(entry => ({
    key: entry.id,
    gridTemplate: [
      ...entry.sections.map(section => `${section.widthPx}px`),
      '1fr',
      ...entry.cards.map(() => CARD_TRACK),
    ].join(' '),
    columns: entry.sections.map(section => ({
      heading: section.heading,
      twoColumns: section.twoColumns,
      links: section.items.map(item => ({ label: item.label, href: item.href })),
    })),
    cards: entry.cards.map(card => ({
      image: card.imageUrl ?? '',
      label: card.label,
      href: card.href,
    })),
  }))
}
